import React from 'react';
import styled from 'styled-components';

interface BrandStorySectionProps {
  storyImage?: string;
}

const StoryWrapper = styled.section`
  padding: clamp(80px, 12vw, 140px) 0;
  background: linear-gradient(180deg, #f7f3ee 0%, #fdfaf6 55%, #ffffff 100%);
`;

const StoryInner = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: clamp(40px, 8vw, 80px);
  align-items: center;
`;

const StoryImage = styled.div<{ image?: string }>`
  position: relative;
  border-radius: clamp(24px, 4vw, 36px);
  background: ${({ image }) =>
    image
      ? `linear-gradient(155deg, rgba(25, 30, 27, 0.45), rgba(25, 30, 27, 0.2)), url('${image}') center/cover`
      : 'linear-gradient(155deg, rgba(47, 58, 52, 0.85), rgba(102, 87, 73, 0.65))'};
  min-height: clamp(320px, 35vw, 420px);
  overflow: hidden;
  box-shadow: 0 25px 45px rgba(36, 43, 39, 0.18);

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(20, 25, 22, 0.25), transparent 35%, rgba(20, 25, 22, 0.3));
  }
`;

const StoryBadge = styled.div`
  position: absolute;
  bottom: 24px;
  left: 24px;
  padding: 20px 24px;
  border-radius: 18px;
  background: rgba(247, 243, 238, 0.95);
  color: #2f3a34;
  width: clamp(220px, 60%, 260px);
  display: grid;
  gap: 6px;
  box-shadow: 0 18px 36px rgba(36, 43, 39, 0.2);

  span:first-child {
    font-size: 0.85rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(47, 58, 52, 0.7);
  }

  span:last-child {
    font-size: 1.1rem;
    font-weight: 600;
    line-height: 1.6;
  }
`;

const StoryContent = styled.div`
  display: grid;
  gap: clamp(18px, 3vw, 28px);
`;

const StoryTitle = styled.h2`
  font-size: clamp(2rem, 3.2vw, 2.8rem);
  font-weight: 600;
  color: #2c2a29;
  line-height: 1.2;
  letter-spacing: -0.01em;
`;

const StoryBody = styled.p`
  font-size: 1.05rem;
  line-height: 1.8;
  color: rgba(44, 42, 41, 0.75);
  max-width: 620px;
`;

const Signature = styled.div`
  display: grid;
  gap: 6px;
  color: #2f3a34;

  span {
    font-size: 1rem;
    font-weight: 600;
  }
`;

const StoryHighlights = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 20px;
  margin-top: 12px;
`;

const HighlightCard = styled.div`
  padding: 18px 20px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(227, 221, 213, 0.7);
  box-shadow: 0 12px 24px rgba(37, 45, 40, 0.08);

  strong {
    display: block;
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 6px;
    color: #2f3a34;
  }

  span {
    font-size: 0.9rem;
    line-height: 1.6;
    color: rgba(47, 58, 52, 0.65);
  }
`;

const highlights = [
  { title: 'Butiken i Kramfors', text: 'Besök oss på Torggatan 1 för personlig rådgivning och unika fynd.' },
  { title: 'Floristateljé', text: 'Vi binder brudbuketter, sorgearrangemang och presenter efter önskemål.' },
  { title: 'Hållbart fokus', text: 'Leverantörer vi tror på, med omtanke om kvalitet och miljö.' }
];

const BrandStorySection: React.FC<BrandStorySectionProps> = ({ storyImage }) => {
  return (
    <StoryWrapper>
      <div className="container">
        <StoryInner>
          <StoryImage image={storyImage}>
            <StoryBadge>
              <span>Inredningsstudio & florist</span>
              <span>
                Ett varmt, ombonat rum där ljus, grönt och handplockad inredning skapar en känsla av hemma.
              </span>
            </StoryBadge>
          </StoryImage>
          <StoryContent>
            <StoryTitle>En mysig butik i hjärtat av Kramfors</StoryTitle>
            <StoryBody>
              Här blandar vi klassisk floristkonst med personligt utvald inredning. Vi dukar upp stämningar,
              kombinerar dofter och väljer texturer som gör vardagen lite mjukare. Oavsett om du letar efter
              helgens bukett, en gåva eller något som förhöjer hemmets atmosfär hjälper vi dig gärna att hitta rätt.
            </StoryBody>
            <Signature>
              <span>Carola, grundare</span>
            </Signature>
            <StoryHighlights>
              {highlights.map(item => (
                <HighlightCard key={item.title}>
                  <strong>{item.title}</strong>
                  <span>{item.text}</span>
                </HighlightCard>
              ))}
            </StoryHighlights>
          </StoryContent>
        </StoryInner>
      </div>
    </StoryWrapper>
  );
};

export default BrandStorySection;
